import {
  apiPostForm,
  apiPostFormWithUploadProgress,
  apiPostJsonWithUploadProgress,
  type UploadProgressHandlers,
} from "./http";

export type MembersImportPreviewResult = {
  ok: boolean;
  rows: number;
  errors: { row: number; field?: string; message: string }[];
  recommendedHeaders?: string[];
};

/** 与后端 members/import/analyze 单行一致 */
export type MembersImportAnalyzeRow = {
  row: number;
  memberNo: string;
  /** 模板 memberNo 为空时，预检阶段按租户规则预生成的编号（导入时重新正式分配） */
  memberNoAutoGenerated?: boolean;
  realName: string;
  phoneDigits: string;
  idNumber: string;
  levelName: string;
  city: string;
  /** city 含省市区时解析出的居住地区（省 / 市 / 区） */
  addressRegion?: string;
  /** city 含省市区时解析出的详细地址 */
  addressDetail?: string;
  organization?: string;
  politicalStatus?: string;
  occupation?: string;
  emergencyContact?: string;
  emergencyContactPhone?: string;
  /** CSV 原始值；空表示有效会员 */
  status?: string;
  /** CSV 原始值；空或 0 表示不发放初始积分 */
  initialPoints?: string;
  /** 解析后的初始积分（非负整数） */
  initialPointsParsed?: number;
  /** CSV 原始值；会员分组展示名，空表示不分配 */
  memberGroupName?: string;
  /** 解析后的分组名（字典命中或自动新建后） */
  memberGroupResolvedName?: string;
  /** CSV 原始值；分组职位展示名 */
  memberGroupRoleName?: string;
  /** 解析后的职位名 */
  memberGroupRoleResolvedName?: string;
  /** CSV 原始值；衣服尺码，空表示不写入 */
  clothingSize?: string;
  /** 写入时将使用的大写尺码 */
  clothingSizeNormalized?: string;
  /** CSV 原始值；会费金额（元），空按 0，不取等级字典 */
  duesAmountYuan?: string;
  /** 解析后的会费金额（元） */
  duesAmountYuanParsed?: number;
  /** 0=有效 1=过期；-1=无效 */
  statusCode?: number;
  statusLabel?: string;
  parsedGender: string;
  parsedBirthDate: string | null;
  phoneLengthOk: boolean;
  idFormatValid: boolean;
  realnameVerifyConfigured: boolean;
  realnameVerifyMatched: boolean | null;
  realnameVerifyMessage: string;
  remark: string;
  defaultSelected: boolean;
};

export type MembersImportAnalyzeResult = {
  rows: MembersImportAnalyzeRow[];
  recommendedHeaders: string[];
};

export type MemberImportCommitRowPayload = {
  memberNo: string;
  realName: string;
  phone: string;
  idNumber: string;
  levelName: string;
  city: string;
  /** 0=有效会员，1=过期会员；空或不传默认为 0 */
  status?: string;
  /** 会员初始积分；空或 0 表示不发放 */
  initialPoints?: string;
  /** 会员分组名称（不含组内职位）；空表示不分配；不存在时预检会自动建字典 */
  memberGroupName?: string;
  /** 分组职位名称；空表示不设职位；不存在时自动补到该组 */
  memberGroupRoleName?: string;
  /** 衣服尺码；空表示不写入，导入时自动转大写 */
  clothingSize?: string;
  organization?: string;
  politicalStatus?: string;
  occupation?: string;
  emergencyContact?: string;
  emergencyContactPhone?: string;
  /** 会费金额（元）；空或未传按 0，写入缴费记录时不取等级字典金额 */
  duesAmountYuan?: string;
};

/** 与后端 members/import/commit 成功响应 details 项一致 */
export type MemberImportDetailRow = {
  row: number;
  operation: "inserted" | "updated";
  memberLegacyId: string;
  memberNo: string;
  realName: string;
  phone: string;
  idNumber: string;
  parsedGender?: string;
  parsedBirthDate?: string;
  levelName: string;
  city: string;
  addressRegion?: string;
  addressDetail?: string;
  organization?: string;
  politicalStatus?: string;
  occupation?: string;
  emergencyContact?: string;
  emergencyContactPhone?: string;
  status?: number;
  statusLabel?: string;
  memberGroupName?: string;
  memberGroupRoleName?: string;
  clothingSize?: string;
  duesAmountYuan?: number;
  applicationLegacyId: string;
  points: number;
  initialPointsApplied?: number;
  welcomePointsApplied?: number;
};

export type MembersImportCommitResult =
  | {
      ok: true;
      inserted: number;
      updated: number;
      applicationsUpserted: number;
      welcomePointsPerNewMember: number;
      details: MemberImportDetailRow[];
    }
  | {
      ok: false;
      inserted: number;
      updated: number;
      applicationsUpserted?: number;
      welcomePointsPerNewMember?: number;
      details?: MemberImportDetailRow[];
      errors: { row: number; message: string }[];
    };

export async function previewMembersImportCsv(
  tenantLegacyId: string,
  file: File,
): Promise<MembersImportPreviewResult> {
  const fd = new FormData();
  fd.append("file", file);
  return apiPostForm<MembersImportPreviewResult>(
    `/api/tenants/${encodeURIComponent(tenantLegacyId)}/members/import/preview`,
    fd,
  );
}

export async function analyzeMembersImportCsv(
  tenantLegacyId: string,
  file: File,
  handlers?: UploadProgressHandlers,
): Promise<MembersImportAnalyzeResult> {
  const fd = new FormData();
  fd.append("file", file);
  return apiPostFormWithUploadProgress<MembersImportAnalyzeResult>(
    `/api/tenants/${encodeURIComponent(tenantLegacyId)}/members/import/analyze`,
    fd,
    handlers,
  );
}

export type MembersImportCommitOptions = UploadProgressHandlers & {
  /** 为 true（默认）时逐条调用身份证二要素实名接口 */
  requireEidVerify?: boolean;
};

export async function commitMembersImportRows(
  tenantLegacyId: string,
  rows: MemberImportCommitRowPayload[],
  options?: MembersImportCommitOptions,
): Promise<MembersImportCommitResult> {
  const { requireEidVerify = true, ...handlers } = options ?? {};
  return apiPostJsonWithUploadProgress<MembersImportCommitResult>(
    `/api/tenants/${encodeURIComponent(tenantLegacyId)}/members/import/commit`,
    { rows, requireEidVerify },
    handlers,
  );
}
